import { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { PrivateRoute } from './component/PrivateRoute';
import { RootRedirect } from './component/rootRedirect/RootRedirect';
import { BatchEnrollment } from './component/tools/BatchEnrollment';
import { GroupSummaryMap } from './component/vehicle/GroupSummaryMap';
import AuthLayout from './layouts/Auth';
import DashboardLayout from './layouts/Dashboard';
import FieldServiceLayout from './layouts/FieldService';
import { About } from './page/about/About';
import { AggregateDeviceEvents } from './page/aggregateDeviceEvents/AggregateDeviceEvents';
import { AlertEvents } from './page/alertEvents/AlertEvents';
import { Batch } from './page/batch/Batch';
import { BatchRuns } from './page/batch/BatchRuns';
import { ReportDetails } from './page/batch/ReportDetails';
import ChangePassword from './page/changePasword/ChangePassword';
import { Configuration } from './page/configuration/Configuration';
import { Customer } from './page/customers/Customer';
import { Customers } from './page/customers/Customers';
import { Dashboard } from './page/dashboard/Dashboard';
import { DelayedPayloads } from './page/delayedPayloads/DelayedPayloads';
import { DeviceEvents } from './page/deviceEvents/DeviceEvents';
import { DeviceDetails } from './page/devices/DeviceDetails';
import { Devices } from './page/devices/Devices';
import Direct from './page/direct/Direct';
import { AssignedRunning } from './page/embeddedBuilds/AssignedRunning';
import { EmbeddedBuilds } from './page/embeddedBuilds/EmbeddedBuilds';
import { EmbeddedBuildsDetails } from './page/embeddedBuilds/EmbeddedBuildsDetails';
import { QueuedUpdates } from './page/embeddedBuilds/QueuedUpdates';
import { SyncResults } from './page/embeddedBuilds/SyncResults';
import Enroll from './page/enroll/Enroll';
import Page404 from './page/errors/Page404';
import Page500 from './page/errors/Page500';
import { Deliver } from './page/fieldService/Deliver';
import { FieldServiceHome } from './page/fieldService/FieldServiceHome';
import { Pickup } from './page/fieldService/Pickup';
import { Recover } from './page/fieldService/Recover';
import { FleetTracking } from './page/fleetTracking/FleetTracking';
import { Help } from './page/help/Help';
import Journal from './page/journal/Journal';
import { LocationVisit } from './page/locationVisits/LocationVisit';
import { LocationVisits } from './page/locationVisits/LocationVisits';
import { ToolUsageHolder } from './page/locationVisits/ToolUsageHolder';
import Maintenance from './page/maintenance/Maintenance';
import { Manufacturer } from './page/manufacturers/Manufacturer';
import { Manufacturers } from './page/manufacturers/Manufacturers';
import { OperationDashboard } from './page/operationDashboard/OperationDashboard';
import { OperationStatus } from './page/operationStatus/OperationStatus';
import { AdminOrganizationsList } from './page/organizations/AdminList';
import { AdminOrganizationDetails } from './page/organizations/AdminOrganizationDetails';
import { PayloadDetails } from './page/payloads/PayloadDetails';
import { PayloadsList } from './page/payloads/Payloads';
import { PayloadsSubmitted } from './page/payloads/PayloadsSubmitted';
import { PhantomOnRoadPayloads } from './page/phantomOnRoadPayloads/PhantomOnRoadPayloads';
import Privacy from './page/privacy/Privacy';
import { Product } from './page/products/Product';
import { Products } from './page/products/Products';
import { QrCodeTest } from './page/qrcode/QrCodeTest';
import { Reports } from './page/reporting/Reports';
import PasswordResetConfirm from './page/resetPassword/PasswordResetConfirm';
import PasswordResetRequest from './page/resetPassword/PasswordResetRequest';
import ResetPassword from './page/resetPassword/ResetPassword';
import { Rooms } from './page/rooms/Rooms';
import { ToolRoom } from './page/rooms/ToolRoom';
import { ScanEvents } from './page/scanEvents/ScanEvents';
import Communications from './page/settings/Communications';
import SignIn from './page/signIn/SignIn';
import TwoFA from './page/signIn/TwoFA';
import SignOut from './page/signOut/SignOut';
import { JobSiteDashboard } from './page/sites/JobSiteDashboard';
import { Site } from './page/sites/Site';
import { Sites } from './page/sites/Sites';
import { ActiveJobSites } from './page/staging/ActiveJobSites';
import { Assignments } from './page/staging/Assignments';
import { RecoveryProgress } from './page/staging/RecoveryProgress';
import { StockInstalled } from './page/stock/StockInstalled';
import { StockItems } from './page/stock/StockItems';
import { JobSiteSummary } from './page/summary/JobSiteSummary';
import { TagHealth } from './page/tagHealth/TagHealth';
import { AdminToolDetails } from './page/tools/AdminToolDetails';
import { RecoveredJobs } from './page/tools/RecoveredJobs';
import { ToolDetails } from './page/tools/ToolDetails';
import { Tools } from './page/tools/Tools';
import { ToolsDashboard } from './page/tools/ToolsDashboard';
import { ToolsManufacturers } from './page/tools/ToolsManufacturers';
import { ToolsProducts } from './page/tools/ToolsProducts';
import { ToolsActivity } from './page/tools/activity/ToolsActivity';
import { ToolsActivityDetail } from './page/tools/activity/ToolsActivityDetail';
import Tracking from './page/tracking/Tracking';
import Usage from './page/usage/Usage';
import { AdminUsersList } from './page/users/AdminList';
import { UserDetails } from './page/users/UserDetails';
import { Users } from './page/users/Users';
import { VehicleTracking } from './page/vehicleTracking/VehicleTracking';
import { VehicleDetails } from './page/vehicles/VehicleDetails';
import Vehicles from './page/vehicles/Vehicles';
import { MarketDetails } from './page/verticalMarkets/MarketDetails';
import VerticalMarkets from './page/verticalMarkets/VerticalMarkets';
import { VerticalMarketsFeatureDetails } from './page/verticalMarkets/VerticalMarketsFeatureDetails';
import { VerticalMarketsFeatures } from './page/verticalMarkets/VerticalMarketsFeatures';
import { Card } from './styled/components';

export type Route = {
  path: string;
  element: ReactNode;
  title?: string;
  children?: Route[];
  refresh?: boolean;
  refreshManual?: boolean;
  redirectTo?: string;
  accessRoles?: string[];
  accessFeatures?: string[];
  accessFeaturesNot?: string[];
};

const routes = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/activity/*',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Activity',
    children: [
      {
        path: '',
        element: <Navigate to="/activity/dashboard/summary" />,
        title: 'Dashboard',
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
        title: 'Dashboard',
        children: [
          {
            path: 'summary',
            element: <Outlet />,
            title: 'Summary',
            refresh: true,
            refreshManual: true,
          },
          {
            path: 'summary/:tab',
            element: <Outlet />,
            title: 'Summary',
            refresh: true,
            refreshManual: true,
          },
        ],
      },
      // A sibling of dashboard rather than a child of it: the Dashboard page
      // renders no Outlet, so a child route could never show its own element.
      // Remediation only; the menu hides the tab for other markets.
      {
        path: 'dashboard/activity',
        element: <ToolsActivity />,
        title: 'Activity',
        refresh: true,
      },
      {
        path: 'dashboard/activity/:grain/:start',
        element: <ToolsActivityDetail />,
        title: 'Activity',
        refresh: true,
      },
      {
        path: 'map',
        element: <GroupSummaryMap withTraffic withFullscreen />,
        title: 'Map',
        refresh: true,
      },
      {
        path: 'pipeline',
        element: <JobSiteSummary />,
        title: 'Pipeline',
        refresh: true,
      },
      {
        path: 'tool-usage',
        element: <ToolUsageHolder />,
        title: 'Tool Usage',
      },
      {
        path: 'stock-installed',
        element: <StockInstalled withPreset />,
        title: 'Stock Installed',
      },
      {
        path: 'site-visits',
        element: <LocationVisits withFilters />,
        title: 'Site Visits',
      },
      {
        path: 'site-visits/:id/details',
        element: <LocationVisit />,
        title: 'Site Visit',
      },
      {
        path: 'site-visits/:id/details/:tab',
        element: <LocationVisit />,
        title: 'Site Visit',
      },
      {
        path: 'vehicle-tracking',
        element: <VehicleTracking />,
        title: 'Vehicle Tracking',
        refreshManual: true,
        accessFeatures: ['Vehicle Tracking'],
      },
      {
        path: 'fleet-tracking',
        element: <FleetTracking />,
        title: 'Fleet Tracking',
        refreshManual: true,
        accessFeatures: ['Fleet Tracking'],
      },
    ],
  },
  {
    path: '/tools/*',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Tools',
    children: [
      {
        path: '',
        element: <Navigate to="/tools/dashboard" />,
        title: 'Dashboard',
      },
      {
        path: 'dashboard',
        element: <ToolsDashboard />,
        title: 'Tools Dashboard',
      },
      {
        path: 'items',
        element: <Tools />,
        title: 'Tools',
      },
      {
        path: 'items/:id/details',
        element: <ToolDetails type="tools" />,
        title: 'Tool',
      },
      {
        path: 'items/:id/details/:tab',
        element: <ToolDetails type="tools" />,
        title: 'Tool',
      },
      {
        path: 'products',
        element: <ToolsProducts />,
        title: 'Products',
      },
      {
        path: 'products/:id/details',
        element: <Product type="tools" />,
        title: 'Product',
      },
      {
        path: 'products/:id/details/:tab',
        element: <Product type="tools" />,
        title: 'Product',
      },
      {
        path: 'manufacturers',
        element: <ToolsManufacturers />,
        title: 'Manufacturers',
        accessFeaturesNot: ['Container'],
      },
      {
        path: 'manufacturers/:id/details',
        element: <Manufacturer />,
        title: 'Manufacturer',
        accessFeaturesNot: ['Container'],
      },
      {
        path: 'reports',
        element: <Reports category="product-supervisor" />,
        title: 'Reports',
      },
      {
        path: 'batch-enrollment',
        element: <BatchEnrollment />,
        title: 'Batch Enrollment',
        accessFeatures: ['Container'],
      },
    ],
  },
  {
    path: '/stock/*',
    accessRoles: ['Stock'],
    element: (
      <PrivateRoute path="">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Stock',
    children: [
      {
        path: '',
        element: <Navigate to="/stock/items" />,
        title: 'Items',
      },
      {
        path: 'items',
        element: <StockItems withFilters withProduct withManufacturer withNew />,
        title: 'Items',
      },
      {
        path: 'items/:id/details',
        element: <ToolDetails type="stock" />,
        title: 'Item',
      },
      {
        path: 'items/:id/details/:tab',
        element: <ToolDetails type="stock" />,
        title: 'Item',
      },

      {
        path: 'products',
        element: <Products type="stock" />,
        title: 'Products',
      },
      {
        path: 'products/:id/details',
        element: <Product type="stock" />,
        title: 'Product',
      },
      {
        path: 'products/:id/details/:tab',
        element: <Product type="stock" />,
        title: 'Product',
      },
      {
        path: 'manufacturers',
        element: <Manufacturers type="stock" />,
        title: 'Manufacturers',
      },
      {
        path: 'manufacturers/:id/details',
        element: <Manufacturer />,
        title: 'Manufacturer',
      },
      {
        path: ':id/details',
        element: <ToolDetails />,
        title: 'Stock',
      },
    ],
  },
  {
    path: '/events/*',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Events',
    children: [
      {
        path: 'alert-events',
        element: <AlertEvents />,
        title: 'Alert Events',
      },
      {
        path: 'maintenance',
        element: <Maintenance />,
        title: 'Maintenance',
      },
      {
        path: 'tag-health',
        element: <TagHealth />,
        title: 'Tag Health',
      },
    ],
  },
  {
    path: '/customers/*',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/customers">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Customers',
    children: [
      {
        path: 'customers',
        element: <Customers />,
        title: 'Customers',
      },
      {
        path: 'customers/:id/details',
        element: <Customer />,
        title: 'Customer',
      },
      {
        // Remediation only; the menu hides it elsewhere (see remediationMenu).
        path: 'dashboard',
        element: <JobSiteDashboard />,
        title: 'Dashboard',
        refresh: true,
      },
      {
        path: 'job-sites',
        element: <Sites />,
        title: 'Job Sites',
      },
      {
        path: 'job-sites/:id/details',
        element: <Site />,
        title: 'Site',
      },
      {
        path: 'job-sites/:id/details/:tab',
        element: <Site />,
        title: 'Site',
      },
      {
        path: 'open-job-sites',
        element: <ActiveJobSites />,
        title: 'Open Job Sites',
      },
      {
        path: 'closed-job-site',
        element: (
          <Card sx={{ py: 0 }}>
            <RecoveredJobs dense withExport />
          </Card>
        ),
        title: 'Closed Job Site',
        accessFeatures: ['Forced Delivery'],
      },
      {
        path: 'recovery-progress',
        element: <RecoveryProgress />,
        title: 'Recovery Progress',
      },
    ],
  },
  {
    path: '/company/*',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/company">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Your Company',
    children: [
      {
        path: '',
        element: <Navigate to="/company/vehicles" />,
        title: 'Company',
      },
      {
        path: 'vehicles',
        element: <Vehicles />,
        title: 'Fleet Vehicles',
      },
      {
        path: 'vehicles/:id/details',
        element: <VehicleDetails />,
        title: 'Vehicle',
      },
      {
        path: 'vehicles/:id/details/:tab',
        element: <VehicleDetails />,
        title: 'Vehicle',
      },
      {
        path: 'users',
        element: <Users />,
        title: 'Users',
      },
      {
        path: 'users/:id/details',
        element: <UserDetails />,
        title: 'User Details',
      },
      {
        path: 'users/current',
        element: <UserDetails current />,
        title: 'Current User',
      },
      {
        path: 'users/current/change-password',
        element: <ChangePassword />,
        title: 'Change Password',
      },
      {
        path: 'sites',
        element: <Sites type="organization" />,
        title: 'Company Sites',
      },
      {
        path: 'sites/:id/details',
        element: <Site type="organization" />,
        title: 'Company Site Details',
      },
      {
        path: 'sites/:id/details/:tab',
        element: <Site type="organization" />,
        title: 'Company Site Details',
      },
      {
        path: 'rooms',
        element: <Rooms />,
        title: 'Company Rooms',
      },
      {
        path: 'rooms/:id/details',
        element: <ToolRoom />,
        title: 'Tool Room',
      },
      {
        path: 'rooms/:id/details/:tab',
        element: <ToolRoom />,
        title: 'Tool Room',
      },
      {
        path: 'journal',
        element: <Journal />,
        title: 'Journal',
        accessRoles: ['Supervisor', 'Admin'],
      },
    ],
  },
  {
    path: '/delivery/*',
    accessFeatures: ['Forced Delivery'],
    element: (
      <PrivateRoute path="/delivery">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Delivery',
    children: [
      {
        path: '',
        element: <Navigate to="/delivery/prepared" />,
        title: 'Prepared',
      },
      {
        path: 'prepared',
        element: <Assignments delivered={false} />,
        title: 'Prepared',
      },
      {
        path: 'delivered',
        element: <Assignments delivered />,
        title: 'Delivered',
      },
      {
        path: 'reports',
        element: <Reports category="staged-jobsites" />,
        title: 'Reports',
      },
    ],
  },
  {
    path: '/payloads/:id/details',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/payloads/:id/details">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Payloads',
    children: [
      {
        path: '',
        element: <PayloadDetails />,
        title: 'Payload Details',
      },
      {
        path: ':tab',
        element: <PayloadDetails />,
        title: 'Payload Details',
      },
    ],
  },
  {
    path: '/warehouse/:id',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/warehouse/:id">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Warehouse',
    children: [
      {
        path: 'payloads',
        element: <PayloadsList />,
        title: 'Warehouse Payloads List',
      },
      {
        path: 'site-visits',
        element: <LocationVisits withFilters />,
        title: 'Warehouse Site Visits',
      },
    ],
  },
  {
    path: '/configuration/*',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/configuration">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Configuration',
    children: [
      {
        path: '',
        element: <Configuration />,
        title: 'Configuration',
      },
    ],
  },
  {
    path: '/reporting',
    accessRoles: ['Supervisor'],
    element: (
      <PrivateRoute path="/reporting">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Reporting',
    children: [
      {
        path: '',
        element: <Navigate to="/reporting/reports" />,
      },
      {
        path: 'reports',
        element: <Reports category="supervisor" />,
        title: 'Reports',
      },
      {
        path: 'reports/:id',
        element: <Reports category="supervisor" />,
        title: 'Report',
      },
      {
        path: 'batch',
        element: <Batch />,
        title: 'Batch',
      },
      {
        path: 'batch/:id/details',
        element: <ReportDetails />,
        title: 'Report',
      },
      {
        path: 'batch/:id/details/:tab',
        element: <ReportDetails />,
        title: 'Report',
      },
    ],
  },
  {
    path: '/help/*',
    element: (
      <PrivateRoute path="/help">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Help',
    children: [
      {
        path: '',
        element: <Help />,
        title: 'Help',
      },
    ],
  },
  {
    path: '/about/*',
    element: (
      <PrivateRoute path="/about">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'About',
    children: [
      {
        path: '',
        element: <About />,
        title: 'About',
      },
    ],
  },
  // next

  {
    path: '/enroll',
    element: <AuthLayout />,
    title: 'Enroll',
    children: [
      {
        path: ':id',
        element: <Enroll />,
      },
    ],
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'sign-in',
        element: <SignIn />,
      },
      {
        path: '2fa',
        element: <TwoFA />,
      },
      {
        path: 'sign-out',
        element: <SignOut />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: '404',
        element: <Page404 />,
      },
      {
        path: '500',
        element: <Page500 />,
      },
    ],
  },
  {
    path: '/password',
    element: <AuthLayout />,
    children: [
      {
        path: 'reset/request',
        element: <PasswordResetRequest />,
      },
      {
        path: 'reset/confirm/email/:encodedEmail/code/:code',
        element: <PasswordResetConfirm />,
      },
    ],
  },
  {
    path: '/admin/operations/*',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/operations">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Operations',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/operations/status" />,
      },
      {
        path: 'status',
        element: <OperationStatus />,
        title: 'Status',
      },
      {
        path: 'dashboard',
        element: <OperationDashboard />,
        title: 'Operations Dashboard',
        refresh: true,
      },
      {
        path: 'aggregate-device-events',
        element: <AggregateDeviceEvents />,
        title: 'Aggregate Device Events',
      },
      {
        path: 'device-events',
        element: <DeviceEvents />,
        title: 'Device Events',
      },
      {
        path: 'payloads-submitted',
        element: <PayloadsSubmitted />,
        title: 'Payloads Submitted',
      },
      {
        path: 'delayed-payloads',
        element: <DelayedPayloads />,
        title: 'Delayed Payloads',
      },
      {
        path: 'phantom-onroad-payloads',
        element: <PhantomOnRoadPayloads />,
        title: 'Phantom OnRoad Payloads',
      },
    ],
  },
  {
    path: '/admin/organizations/*',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/organizations">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Organizations',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/organizations/organizations" />,
      },

      {
        path: 'organizations',
        element: <AdminOrganizationsList />,
        title: 'Organizations',
      },
      {
        path: 'organizations/:id/details',
        element: <AdminOrganizationDetails />,
        title: 'Organization Details',
      },
      {
        path: 'organizations/:id/details/:tab',
        element: <AdminOrganizationDetails />,
        title: 'Organization Details',
      },
      {
        path: 'rooms/:id/details',
        element: <ToolRoom />,
        title: 'Organization Room',
      },
      {
        path: 'rooms/:id/details/:tab',
        element: <ToolRoom />,
        title: 'Organization Room',
      },
      {
        path: 'sites/:id/details',
        element: <Site />,
        title: 'Organization Site',
      },
      {
        path: 'sites/:id/details/:tab',
        element: <Site />,
        title: 'Organization Site',
      },
      {
        path: 'vehicles/:id/details',
        element: <VehicleDetails />,
        title: 'Organization Vehicle',
      },
      {
        path: 'vehicles/:id/details/:tab',
        element: <VehicleDetails />,
        title: 'Organization Vehicle',
      },
      {
        path: 'users',
        element: <AdminUsersList />,
        title: 'Users',
      },
      {
        path: 'users/current',
        element: <UserDetails current />,
        title: 'Current User',
      },
      {
        path: 'users/current/change-password',
        element: <ChangePassword adminUser />,
      },
      {
        path: 'users/:id/details',
        element: <UserDetails adminUser />,
        title: 'User Details',
      },
      {
        path: 'devices',
        element: <Devices />,
        title: 'Devices',
      },
      {
        path: 'devices/:id/details',
        element: <DeviceDetails />,
        title: 'Device Details',
      },
      {
        path: 'devices/:id/details/:tab',
        element: <DeviceDetails />,
        title: 'Device Details',
      },
      {
        path: 'devices/payloads/:id/details',
        element: <PayloadDetails admin />,
        title: 'Device Details',
      },
      {
        path: 'devices/payloads/:id/details/:tab',
        element: <PayloadDetails admin />,
        title: 'Device Details',
      },
    ],
  },
  {
    path: '/admin/products/*',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/products">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Operations',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/products/manufacturers" />,
      },
      {
        path: 'manufacturers',
        element: <Manufacturers />,
        title: 'Manufacturers',
      },
      {
        path: 'manufacturers/:id/details',
        element: <Manufacturer />,
        title: 'Manufacturer',
      },
      {
        path: 'manufacturers/:id/details/:tab',
        element: <Manufacturer />,
        title: 'Manufacturer',
      },
      {
        path: 'products',
        element: <Products />,
        title: 'Products',
      },
      {
        path: 'products/:id/details',
        element: <Product />,
        title: 'Product Details',
      },
      {
        path: 'products/:id/details/:tab',
        element: <Product />,
        title: 'Product Details',
      },
      {
        path: 'reports',
        element: <Reports category="product-admin" />,
        title: 'Reports',
      },
    ],
  },
  {
    path: '/admin/items/*',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/items">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Items',
    children: [
      {
        path: '',
        element: <Navigate to="/" />,
      },

      {
        path: ':id/details',
        element: <AdminToolDetails />,
        title: 'Manufacturer',
      },
    ],
  },
  {
    path: '/admin/embedded-builds/*',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/embedded-builds">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Embedded Builds',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/embedded-builds/assigned-running" />,
      },
      {
        path: 'assigned-running',
        element: <AssignedRunning />,
        title: 'Assigned & Running',
      },
      {
        path: 'builds',
        element: <EmbeddedBuilds />,
        title: 'Builds',
      },
      {
        path: 'sync-results',
        element: <SyncResults />,
        title: 'Sync Results',
      },
      {
        path: 'builds/:id/details',
        element: <EmbeddedBuildsDetails />,
        title: 'Builds Details',
      },
      {
        path: 'queued-device-updates',
        element: <QueuedUpdates />,
        title: 'Queued Updates',
      },
    ],
  },
  {
    path: '/admin/usage',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/usage">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Usage',
    children: [
      {
        path: '',
        element: <Usage />,
        title: 'Usage',
      },
    ],
  },
  {
    path: '/admin/tracking',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/tracking">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Tracking',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/tracking/email" />,
      },
      {
        path: 'email',
        element: <Tracking channel="email" />,
        title: 'Email',
      },
      {
        path: 'sms',
        element: <Tracking channel="sms" />,
        title: 'SMS',
      },
    ],
  },
  {
    path: '/privacy',
    element: <Privacy />,
    title: 'Privacy Policy',
  },
  {
    path: '/admin/settings',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/settings">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Settings',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/settings/communications" />,
      },
      {
        path: 'communications',
        element: <Communications />,
        title: 'Communications',
      },
    ],
  },
  {
    path: '/admin/vertical-markets',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/vertical-markets">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Vertical Markets',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/vertical-markets/markets" />,
      },
      {
        path: 'markets',
        element: <VerticalMarkets />,
        title: 'Markets',
      },
      {
        path: 'markets/:id/details',
        element: <MarketDetails />,
        title: 'Market Details',
      },
      {
        path: 'markets/:id/details/:tab',
        element: <MarketDetails />,
        title: 'Market Details',
      },
      {
        path: 'features',
        element: <VerticalMarketsFeatures />,
        title: 'Features',
      },
      {
        path: 'features/:id/details',
        element: <VerticalMarketsFeatureDetails />,
        title: 'Feature Details',
      },
    ],
  },
  {
    path: '/admin/sites',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/sites">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Recycle Sites',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/sites/recycle" />,
      },
      {
        path: 'recycle',
        element: <Sites type="recycle" />,
        title: 'Recycle Sites',
      },
      {
        path: 'recycle/:id/details',
        element: <Site type="recycle" />,
        title: 'Site',
      },
      {
        path: 'recycle/:id/details/:tab',
        element: <Site type="recycle" />,
        title: 'Site',
      },
    ],
  },
  {
    path: '/admin/reporting',
    accessRoles: ['Admin'],
    element: (
      <PrivateRoute path="/admin/reporting">
        <DashboardLayout />
      </PrivateRoute>
    ),
    title: 'Reporting',
    children: [
      {
        path: '',
        element: <Navigate to="/admin/reporting/reports" />,
      },
      {
        path: 'reports',
        element: <Reports category="operations" />,
        title: 'Reports',
      },
      {
        path: 'reports/:id',
        element: <Reports category="operations" />,
        title: 'Report',
      },
      {
        path: 'batch',
        element: <Batch />,
        title: 'Batch',
      },
      {
        path: 'batch/:id/details',
        element: <ReportDetails />,
        title: 'Report',
      },
      {
        path: 'batch/:id/details/:tab',
        element: <ReportDetails />,
        title: 'Report',
      },
      {
        path: 'batch-report-runs',
        element: <BatchRuns />,
        title: 'Batch Runs',
      },
    ],
  },
  {
    path: '/scan-events',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/scan-events">
        <DashboardLayout />
      </PrivateRoute>
    ),
    children: [
      {
        path: '',
        element: <ScanEvents withFilters />,
        title: 'Scan Events',
      },
    ],
  },
  {
    path: '/field-service/*',
    accessRoles: ['Supervisor', 'Operator', 'Contractor'],
    element: (
      <PrivateRoute path="/field-service">
        <FieldServiceLayout />
      </PrivateRoute>
    ),
    title: 'Field Service',
    children: [
      {
        path: '',
        element: <FieldServiceHome />,
        title: 'Field Service',
      },
      {
        path: 'pickup',
        element: <Pickup />,
        title: 'Pickup',
      },
      {
        path: 'deliver',
        element: <Deliver />,
        title: 'Deliver',
      },
      {
        path: 'deliver/:assignmentId',
        element: <Deliver />,
        title: 'Deliver',
      },
      {
        path: 'recover',
        element: <Recover />,
        title: 'Recover',
      },
    ],
  },
  {
    path: '/qrcode',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/qrcode">
        <DashboardLayout />
      </PrivateRoute>
    ),
    children: [
      {
        path: '',
        element: <QrCodeTest />,
        title: 'QR Code Test',
      },
    ],
  },
  {
    path: '/search/:id',
    accessRoles: ['Supervisor', 'Operator', 'Stock'],
    element: (
      <PrivateRoute path="/search/:id">
        <Direct />
      </PrivateRoute>
    ),
    title: 'Warehouse',
    redirectTo: 'https://www.thetagsystem.com',
  },
  {
    path: '*',
    element: <AuthLayout />,
    children: [
      {
        path: '*',
        element: <Page404 />,
      },
    ],
  },
] as Route[];

export default routes;
